import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Styled popup dropdown — the pretty button + panel UI previously hand-rolled
// only for the planner's "Shared transport options". Now a reusable primitive:
// supports single-select (click picks + closes, check on the active row) and
// multi-select (`multiple`, checkboxes, stays open, trigger shows a count).
// Reuse this for any future dropdown instead of a bare <select>.
//
// Docs (API, caller list, gotchas): docs/codebase/Dropdown.md — keep in sync.

export interface DropdownOption {
  value: string;
  label: React.ReactNode;
  /** Shown greyed and not pickable (e.g. a transport mode this leg cannot use). */
  disabled?: boolean;
  /** A short second line under the label, e.g. why the option is disabled
   *  ("No direct night train"). Stays readable on a disabled option. */
  hint?: React.ReactNode;
  /** The row does something instead of being picked: clicking runs this and
   *  closes the menu, the value does not change. With `actionLabel`, a third
   *  line in the accent colour says what ("Search night trains"). */
  onSelect?: () => void;
  actionLabel?: React.ReactNode;
}

interface BaseProps {
  options: DropdownOption[];
  placeholder?: string;
  ariaLabel?: string;
  disabled?: boolean;
  // Optional sticky action pinned at the bottom of the menu (outside the
  // scroll area). Selecting it runs onSelect and closes the menu — used for
  // escape-hatch actions.
  footer?: { label: React.ReactNode; onSelect: () => void };
  // Single-select only: hide the leading ✓ indicator column (and its spacing)
  // so options use the full width — for action menus where no item is "current".
  hideCheck?: boolean;
  /**
   * Trigger look. 'field' (default) is the bordered, full-width form control.
   * 'bare' drops the border/background/width so the trigger is just its label
   * plus the caret — for placing a picker inline in dense UI (e.g. the leg
   * card's transport emoji) without redesigning the surface around it.
   */
  variant?: 'field' | 'bare';
  /**
   * Override the trigger's content. By default the trigger shows the selected
   * option's `label`; pass this when the trigger needs a shorter form than the
   * menu row (e.g. emoji only on the trigger, "Train 🚆" in the menu).
   */
  triggerLabel?: React.ReactNode;
  /**
   * Render the menu in a portal on document.body with fixed, viewport-clamped
   * positioning. Required whenever an ancestor creates a stacking context or
   * clips overflow — an absolutely positioned menu is trapped inside it (e.g.
   * RouteTimeline's `.rt-row { position: relative; z-index: 1 }`, where the
   * next row paints over the open menu regardless of its z-index). Off by
   * default so existing callers are untouched.
   */
  portal?: boolean;
}

interface SingleProps extends BaseProps {
  multiple?: false;
  value: string;
  onChange: (value: string) => void;
}

interface MultiProps extends BaseProps {
  multiple: true;
  value: string[];
  onChange: (value: string[]) => void;
}

export type DropdownProps = SingleProps | MultiProps;

/** Portalled-menu geometry: gap to the trigger and breathing room to the edge. */
const MENU_GAP = 4;
const MENU_VIEWPORT_MARGIN = 8;
/** Above dialogs (max 3000), below the fullscreen loader (9999) and toasts. */
const MENU_Z_INDEX = 4000;
/** How long the portalled menu keeps following its trigger after opening. */
const MENU_SETTLE_MS = 450;

const Dropdown: React.FC<DropdownProps> = (props) => {
  const { options, placeholder = 'Select…', ariaLabel, disabled, footer, hideCheck, variant = 'field', triggerLabel: triggerLabelOverride, portal = false } = props;
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; minWidth: number; flipUp: boolean } | null>(null);

  // Close when clicking outside. With `portal` the menu lives outside `ref`'s
  // subtree, so it needs its own containment check or picking an option would
  // count as an outside click.
  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      if (ref.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', onClickOutside);
      return () => document.removeEventListener('mousedown', onClickOutside);
    }
  }, [open]);

  // Anchor the portalled menu to the trigger, and keep it anchored.
  useLayoutEffect(() => {
    if (!portal || !open) {
      setMenuPos(null);
      return;
    }
    const measure = () => {
      const rect = ref.current?.getBoundingClientRect();
      if (!rect) return;
      const menuHeight = menuRef.current?.offsetHeight ?? 0;
      const roomBelow = window.innerHeight - rect.bottom - MENU_VIEWPORT_MARGIN;
      const flipUp = menuHeight > 0 && roomBelow < menuHeight && rect.top - MENU_VIEWPORT_MARGIN > roomBelow;
      const next = {
        top: flipUp ? rect.top - MENU_GAP : rect.bottom + MENU_GAP,
        left: Math.max(MENU_VIEWPORT_MARGIN, Math.min(rect.left, window.innerWidth - rect.width - MENU_VIEWPORT_MARGIN)),
        minWidth: rect.width,
        flipUp,
      };
      setMenuPos((prev) => (prev && prev.top === next.top && prev.left === next.left && prev.minWidth === next.minWidth && prev.flipUp === next.flipUp ? prev : next));
    };
    measure();
    let frame = 0;
    const onViewportChange = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(measure);
    };
    window.addEventListener('scroll', onViewportChange, true);
    window.addEventListener('resize', onViewportChange);
    // A trigger that is still moving when the menu opens (a bottom sheet
    // sliding in) fires neither scroll nor resize: follow it until it rests.
    let settle = 0;
    const settleUntil = performance.now() + MENU_SETTLE_MS;
    const follow = () => {
      measure();
      if (performance.now() < settleUntil) settle = window.requestAnimationFrame(follow);
    };
    settle = window.requestAnimationFrame(follow);
    return () => {
      window.cancelAnimationFrame(frame);
      window.cancelAnimationFrame(settle);
      window.removeEventListener('scroll', onViewportChange, true);
      window.removeEventListener('resize', onViewportChange);
    };
  }, [portal, open, options.length]);

  const isMulti = props.multiple === true;
  const selectedValues: string[] = isMulti ? props.value : props.value ? [props.value] : [];

  const triggerLabel: React.ReactNode = (() => {
    if (isMulti) {
      const n = (props.value as string[]).length;
      return n === 0 ? placeholder : `${n} option${n === 1 ? '' : 's'} selected`;
    }
    if (triggerLabelOverride !== undefined) return triggerLabelOverride;
    const sel = options.find((o) => o.value === props.value);
    return sel ? sel.label : placeholder;
  })();

  const pick = (value: string) => {
    if (isMulti) {
      const cur = props.value as string[];
      props.onChange(cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value]);
    } else {
      (props.onChange as (v: string) => void)(value);
      setOpen(false);
    }
  };

  const bare = variant === 'bare';
  const renderMenu = (node: React.ReactNode) => (portal ? createPortal(node, document.body) : node);

  return (
    <div className={bare ? 'relative inline-flex' : 'relative'} ref={ref}>
      <button
        type="button"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        // The trigger often sits inside a clickable card (the leg pill opens
        // the leg sheet); picking a mode must not also fire that.
        onClick={(e) => { e.stopPropagation(); if (!disabled) setOpen((o) => !o); }}
        className={bare
          ? 'inline-flex items-center gap-0.5 bg-transparent border-0 p-0 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-green-600 rounded disabled:opacity-60 disabled:cursor-not-allowed'
          : 'w-full px-3 py-2 text-left bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex justify-between items-center disabled:opacity-60 disabled:cursor-not-allowed'}
      >
        <span className={bare ? undefined : 'text-gray-700'}>{triggerLabel}</span>
        <svg
          className={`${bare ? 'w-3 h-3 text-gray-500' : 'w-5 h-5 text-gray-400'} transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && renderMenu(
        <div
          ref={menuRef}
          role="listbox"
          className={portal
            ? 'w-max bg-white border border-gray-300 rounded-md shadow-lg'
            : 'absolute z-50 w-max min-w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg'}
          style={portal
            ? (menuPos
              ? {
                position: 'fixed',
                top: menuPos.top,
                left: menuPos.left,
                minWidth: menuPos.minWidth,
                // Flipped: anchor the menu's BOTTOM edge at the trigger's top.
                transform: menuPos.flipUp ? 'translateY(-100%)' : undefined,
                zIndex: MENU_Z_INDEX,
              }
              // First paint before the layout effect measures — keep it out of sight.
              : { position: 'fixed', top: 0, left: 0, visibility: 'hidden', zIndex: MENU_Z_INDEX })
            : undefined}
        >
          <div className="py-1 max-h-60 overflow-y-auto">
            {options.map((option) => {
              const checked = selectedValues.includes(option.value);
              const showIndicator = isMulti || !hideCheck;
              return (
                <div
                  key={option.value}
                  role="option"
                  aria-selected={checked}
                  aria-disabled={option.disabled || undefined}
                  className={option.disabled
                    ? 'px-3 py-2 flex items-center space-x-2 cursor-not-allowed'
                    : 'px-3 py-2 hover:bg-gray-50 cursor-pointer flex items-center space-x-2'}
                  onClick={() => {
                    if (option.disabled) return;
                    if (option.onSelect) { option.onSelect(); setOpen(false); } else pick(option.value);
                  }}
                >
                  <div className="flex items-center">
                    {isMulti ? (
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {}} // handled by the row's onClick
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                    ) : (
                      !hideCheck && <span className="inline-flex items-center justify-center w-4 h-4 text-blue-600 text-sm">{checked ? '✓' : ''}</span>
                    )}
                    <label className={`${showIndicator ? 'ml-2' : ''} text-sm ${option.disabled ? 'cursor-not-allowed' : 'text-gray-700 cursor-pointer'} whitespace-nowrap`}>
                      <span className={option.disabled ? 'text-gray-700 opacity-50' : undefined}>{option.label}</span>
                      {option.hint != null && <span className="block text-xs text-gray-500 whitespace-normal">{option.hint}</span>}
                      {option.onSelect && option.actionLabel != null && (
                        <span className="block text-xs font-semibold text-green-700 whitespace-normal">{option.actionLabel} ›</span>
                      )}
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
          {footer && (
            <button
              type="button"
              className="w-full px-3 py-2 text-left text-sm font-semibold text-green-700 border-t border-gray-200 hover:bg-gray-50 cursor-pointer"
              onClick={() => { footer.onSelect(); setOpen(false); }}
            >
              {footer.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default Dropdown;
